// External
import { skipToken, useQuery } from '@tanstack/react-query';
import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from 'date-fns';

// API
import { getClosedDates, operatingHoursQueryKeys } from '@api/operatingHours';

// Hooks and utilities
import { CALENDAR_WEEK_STARTS_ON } from '@/components/ui/calendar-date-picker';
import { toLocalDateKey } from '@/react/utils/dateTime';

// Types
import type { ClosedDatesResponse, FetchClosedDatesParams } from '@api/operatingHours';

const STALE_TIME_MS = 5 * 60_000;

// Every day the picker grid shows for `month`: whole weeks around the month.
function closedDatesRange(month: Date): FetchClosedDatesParams {
  const options = { weekStartsOn: CALENDAR_WEEK_STARTS_ON };
  return {
    from: toLocalDateKey(startOfWeek(startOfMonth(month), options)),
    to: toLocalDateKey(endOfWeek(endOfMonth(month), options)),
  };
}

function toMap(response: ClosedDatesResponse): Map<string, string[]> {
  return new Map(response.closed_dates.map(({ date, reasons }) => [date, reasons]));
}

export function useClosedDates(month: Date | undefined) {
  const params = month ? closedDatesRange(month) : null;

  return useQuery({
    queryKey: operatingHoursQueryKeys.closedDates(params),
    queryFn: params ? () => getClosedDates(params) : skipToken,
    staleTime: STALE_TIME_MS,
    select: toMap,
  });
}
